import React, { useState, useEffect } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import classNames from 'classnames';

import { TextInput } from 'views/components/TextInput/TextInput';
import { SelectInput } from 'views/components/SelectInput/SelectInput';
import { IContact } from 'types';
import { getErrorMessageFor } from 'tools/utilities/getErrorMessageFor';
import { OPTION_OTHER } from 'store/modules/app/constants';
import { isValidPhone } from 'tools/utilities/validators/phone';
import { isValidEmail } from 'tools/utilities/validators/email';

interface Props {
  isAdditionalContact: boolean;
  intl: IntlShape;
  onChange: (contact: IContact) => void;
  onValid: (isValid: boolean) => void;
  touchedNext: boolean;
  defaultContact?: IContact;
  jobTitles: string[];
}

export const ContactFormComponent = (props: Props) => {
  const {
    isAdditionalContact, intl, onChange, onValid, touchedNext, defaultContact, jobTitles,
  } = props;

  const [firstName, setFirstName] = useState(defaultContact?.firstName || '');
  const [lastName, setLastName] = useState(defaultContact?.lastName || '');
  const [jobTitle, setJobTitle] = useState(defaultContact?.jobTitle || '');
  const [jobOther, setJobOther] = useState(defaultContact?.jobOther || '');
  const [email, setEmail] = useState(defaultContact?.email || '');
  const [contactNumber, setContactNumber] = useState(defaultContact?.contactNumber || '');

  let firstNameId = 'firstName';
  let lastNameId = 'lastName';
  let jobTitleId = 'jobTitle';
  let jobOtherId = 'jobOther';
  let emailId = 'email';
  let contactId = 'contact';

  if (isAdditionalContact) {
    firstNameId += '-additional';
    lastNameId += '-additional';
    jobTitleId += '-additional';
    jobOtherId += '-additional';
    emailId += '-additional';
    contactId += '-additional';
  }

  useEffect(() => {
    onChange({
      firstName,
      lastName,
      jobTitle,
      jobOther,
      email,
      contactNumber,
    });
  }, [firstName, lastName, jobTitle, jobOther, email, contactNumber, onChange]);

  const isOther = jobTitle === OPTION_OTHER;

  const firstNameIsValid = !!firstName;
  const lastNameIsValid = !!lastName;
  const emailIsValid = isValidEmail(email);
  const contactNumberIsValid = isValidPhone(contactNumber);
  const jobTitleIsValid = isOther || !!jobTitle;
  const jobOtherIsValid = isOther && !!jobOther;

  const isValid = firstNameIsValid && lastNameIsValid && emailIsValid && contactNumberIsValid && (jobTitleIsValid || jobOtherIsValid);

  useEffect(() => {
    onValid(isValid);
  }, [onValid, isValid]);

  return (
    <div className="ContactForm">
      <div className="ContactForm-names">
        {/* First name */}
        <TextInput
          id={firstNameId}
          label={intl.formatMessage({ id: 'FIRST_NAME' })}
          type="text"
          placeholder={intl.formatMessage({ id: 'ENTER_FIRST_NAME' })}
          onChange={setFirstName}
          value={firstName}
          error={touchedNext && getErrorMessageFor(firstNameIsValid, isAdditionalContact ? 'ERROR_FIRST_NAME_ADDITIONAL' : 'ERROR_FIRST_NAME', intl)}
        />

        {/* Last name */}
        <TextInput
          id={lastNameId}
          label={intl.formatMessage({ id: 'LAST_NAME' })}
          type="text"
          placeholder={intl.formatMessage({ id: 'ENTER_LAST_NAME' })}
          onChange={setLastName}
          value={lastName}
          error={touchedNext && getErrorMessageFor(lastNameIsValid, isAdditionalContact ? 'ERROR_LAST_NAME_ADDITIONAL' : 'ERROR_LAST_NAME', intl)}
        />
      </div>

      {/* Job title */}
      <div className={classNames('ContactForm-job', { hasOtherInput: isOther })}>
        <SelectInput
          id={jobTitleId}
          label={intl.formatMessage({ id: 'JOB_TITLE' })}
          defaultValue={jobTitle}
          onChange={setJobTitle}
          options={jobTitles}
          placeholder="Select job title"
          error={touchedNext && getErrorMessageFor(jobTitleIsValid, isAdditionalContact ? 'ERROR_JOB_TITLE_ADDITIONAL' : 'ERROR_JOB_TITLE', intl)}
        />

        {isOther && (
          <div className="ContactForm-job-other">
            <TextInput
              type="input"
              id={jobOtherId}
              label={intl.formatMessage({ id: 'JOB_OTHER' })}
              value={jobOther}
              onChange={setJobOther}
              placeholder={intl.formatMessage({ id: 'JOB_OTHER_PLACEHOLDER' })}
              error={touchedNext && getErrorMessageFor(jobOtherIsValid, isAdditionalContact ? 'ERROR_JOB_OTHER_ADDITIONAL' : 'ERROR_JOB_OTHER', intl)}
            />
          </div>
        )}
      </div>

      {/* Email address */}
      <TextInput
        className="TextInput--3of4"
        id={emailId}
        label={intl.formatMessage({ id: 'EMAIL_ADDRESS' })}
        type="email"
        placeholder={intl.formatMessage({ id: 'EMAIL_EXAMPLE' })}
        onChange={setEmail}
        value={email}
        error={touchedNext && getErrorMessageFor(emailIsValid, isAdditionalContact ? 'ERROR_EMAIL_ADDRESS_ADDITIONAL' : 'ERROR_EMAIL_ADDRESS', intl)}
      />

      {/* Preferred contact number */}
      <TextInput
        type="tel"
        className="TextInput--3of4"
        id={contactId}
        label={intl.formatMessage({ id: 'PREFERRED_CONTACT_NUMBER' })}
        placeholder={intl.formatMessage({ id: 'ENTER_NUMBER' })}
        onChange={setContactNumber}
        value={contactNumber}
        error={touchedNext && getErrorMessageFor(contactNumberIsValid, isAdditionalContact ? 'ERROR_CONTACT_NUMBER_ADDITIONAL' : 'ERROR_CONTACT_NUMBER', intl)}
      />
    </div>
  );
};

export const ContactForm = injectIntl(ContactFormComponent);
